// Staff monsters view - monster management

import { css, html } from 'lit';
import { api } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';
import { router } from '../../lib/router.js';

// Pencil icon SVG
const pencilIcon = html`<svg
  xmlns="http://www.w3.org/2000/svg"
  width="16"
  height="16"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  stroke-width="2"
  stroke-linecap="round"
  stroke-linejoin="round"
>
  <path
    d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z"
  />
</svg>`;

// Trash icon SVG
const trashIcon = html`<svg
  xmlns="http://www.w3.org/2000/svg"
  width="16"
  height="16"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  stroke-width="2"
  stroke-linecap="round"
  stroke-linejoin="round"
>
  <path d="M3 6h18" />
  <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
  <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
</svg>`;

// Clipboard icon SVG
const clipboardIcon = html`<svg
  xmlns="http://www.w3.org/2000/svg"
  width="14"
  height="14"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  stroke-width="2"
  stroke-linecap="round"
  stroke-linejoin="round"
>
  <rect width="8" height="4" x="8" y="2" rx="1" ry="1" />
  <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
</svg>`;

// Question icon SVG (for missing image)
const questionIcon = html`<svg
  xmlns="http://www.w3.org/2000/svg"
  width="24"
  height="24"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  stroke-width="2"
  stroke-linecap="round"
  stroke-linejoin="round"
>
  <circle cx="12" cy="12" r="10" />
  <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
  <path d="M12 17h.01" />
</svg>`;

/**
 * Staff monsters view component for managing monsters
 */
class StaffMonstersView extends BaseComponent {
  static properties = {
    _monsters: { type: Array, state: true },
    _loading: { type: Boolean, state: true },
    _error: { type: String, state: true },
    _showDeleteModal: { type: Boolean, state: true },
    _monsterToDelete: { type: String, state: true },
    _crFilter: { type: String, state: true },
    _typeFilter: { type: String, state: true },
  };

  static styles = css`
    :host {
      display: block;
    }

    .monster-image {
      width: 64px;
      height: 64px;
      object-fit: cover;
    }

    .monster-image-placeholder {
      width: 64px;
      height: 64px;
    }
  `;

  constructor() {
    super();
    /** @type {Array<import('../../types/jsdoc-types.js').Monster>} */
    this._monsters = [];
    this._loading = true;
    this._error = null;
    this._showDeleteModal = false;
    this._monsterToDelete = null;
    this._crFilter = '';
    this._typeFilter = '';
  }

  async connectedCallback() {
    await super.connectedCallback();
    this._loadMonsters();
  }

  async _loadMonsters() {
    this._loading = true;
    this._error = null;

    try {
      this._monsters = await api.get('/api/staff/monsters');
    } catch (err) {
      console.error('Failed to load monsters:', err);
      this._error = /** @type {Error} */ (err).message || 'Failed to load monsters';
    } finally {
      this._loading = false;
    }
  }

  get _filteredMonsters() {
    let result = this._monsters;
    if (this._crFilter) {
      result = result.filter((m) => m.cr === this._crFilter);
    }
    if (this._typeFilter) {
      result = result.filter((m) => m.creatureType === this._typeFilter);
    }
    return result;
  }

  _goToNewMonster() {
    router.navigate('/staff/monsters/new');
  }

  /** @param {string} slug */
  _editMonster(slug) {
    router.navigate(`/staff/monsters/${slug}/edit`);
  }

  /** @param {string} slug */
  _deleteMonster(slug) {
    this._monsterToDelete = slug;
    this._showDeleteModal = true;
  }

  async _confirmDelete() {
    if (!this._monsterToDelete) return;

    try {
      await api.delete(`/api/staff/monsters/${this._monsterToDelete}`);
      this._monsterToDelete = null;
      this._showDeleteModal = false;
      await this._loadMonsters();
    } catch (err) {
      console.error('Failed to delete monster:', err);
      this._error = /** @type {Error} */ (err).message || 'Failed to delete monster';
    }
  }

  _cancelDelete() {
    this._showDeleteModal = false;
    this._monsterToDelete = null;
  }

  /** @param {string} type */
  _capitalizeType(type) {
    if (!type) return '';
    return type.charAt(0).toUpperCase() + type.slice(1);
  }

  /**
   * @param {string} text
   * @param {Event} event
   */
  _copyToClipboard(text, event) {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        const btn = /** @type {HTMLElement} */ (event.target).closest('button');
        if (btn) {
          btn.classList.add('btn-success');
          btn.classList.remove('btn-outline-secondary');
          setTimeout(() => {
            btn.classList.remove('btn-success');
            btn.classList.add('btn-outline-secondary');
          }, 2000);
        }
      })
      .catch((err) => {
        console.error('Failed to copy:', err);
      });
  }

  /** @param {Event} e */
  _handleCrChange(e) {
    this._crFilter = /** @type {HTMLSelectElement} */ (e.target).value;
  }

  /** @param {Event} e */
  _handleTypeChange(e) {
    this._typeFilter = /** @type {HTMLSelectElement} */ (e.target).value;
  }

  render() {
    return html`
      <nav aria-label="breadcrumb">
        <ol class="breadcrumb">
          <li class="breadcrumb-item">Staff</li>
          <li class="breadcrumb-item active" aria-current="page">Monsters</li>
        </ol>
      </nav>

      ${this._error ? html` <div class="alert alert-danger">${this._error}</div> ` : ''}

      <div class="d-flex justify-content-between align-items-center mb-3">
        <h2>Monsters</h2>
        <div class="d-flex gap-2 align-items-center">
          <select
            class="form-select form-select-sm"
            style="width: auto;"
            .value="${this._crFilter}"
            @change="${this._handleCrChange}"
          >
            <option value="">All CRs</option>
            <option value="0">CR 0</option>
            <option value="1/8">CR 1/8</option>
            <option value="1/4">CR 1/4</option>
            <option value="1/2">CR 1/2</option>
            <option value="1">CR 1</option>
            <option value="2">CR 2</option>
            <option value="3">CR 3</option>
            <option value="4">CR 4</option>
            <option value="5">CR 5</option>
          </select>
          <select
            class="form-select form-select-sm"
            style="width: auto;"
            .value="${this._typeFilter}"
            @change="${this._handleTypeChange}"
          >
            <option value="">All Types</option>
            <option value="aberration">Aberration</option>
            <option value="beast">Beast</option>
            <option value="celestial">Celestial</option>
            <option value="construct">Construct</option>
            <option value="dragon">Dragon</option>
            <option value="elemental">Elemental</option>
            <option value="fey">Fey</option>
            <option value="fiend">Fiend</option>
            <option value="giant">Giant</option>
            <option value="humanoid">Humanoid</option>
            <option value="monstrosity">Monstrosity</option>
            <option value="ooze">Ooze</option>
            <option value="plant">Plant</option>
            <option value="undead">Undead</option>
          </select>
          <button class="btn btn-primary" @click="${this._goToNewMonster}">Create Monster</button>
        </div>
      </div>

      ${
        this._loading
          ? html`
            <div class="text-center py-4">
              <div class="spinner-border">
                <span class="visually-hidden">Loading...</span>
              </div>
            </div>
          `
          : this._filteredMonsters.length === 0
            ? html` <p class="text-muted">No monsters yet.</p> `
            : html`
              <div class="d-flex flex-wrap gap-3">
                ${this._filteredMonsters.map(
                  (monster) => html`
                    <div style="flex: 1 1 280px; min-width: 260px; max-width: 320px;">
                      <div class="card h-100">
                        <div class="card-body">
                          <div class="d-flex justify-content-between align-items-start mb-2">
                            <div class="d-flex align-items-center gap-2">
                              ${
                                monster.tokenImageUrl
                                  ? html`
                                    <img
                                      src="${api.getImageUrl(monster.tokenImageUrl)}"
                                      alt="${monster.name}"
                                      class="rounded monster-image"
                                    />
                                  `
                                  : html`
                                    <div
                                      class="rounded bg-secondary d-flex align-items-center justify-content-center monster-image-placeholder"
                                    >
                                      <span class="text-white">${questionIcon}</span>
                                    </div>
                                  `
                              }
                              <h6 class="card-title mb-0">${monster.name}</h6>
                            </div>
                            <div class="d-flex gap-1">
                              <button
                                class="btn btn-sm btn-outline-primary"
                                title="Edit"
                                @click="${() => this._editMonster(monster.slug)}"
                              >
                                ${pencilIcon}
                              </button>
                              <button
                                class="btn btn-sm btn-outline-danger"
                                title="Delete"
                                @click="${() => this._deleteMonster(monster.slug)}"
                              >
                                ${trashIcon}
                              </button>
                            </div>
                          </div>

                          <div class="d-flex align-items-center gap-2 mb-2">
                            <code class="small text-muted">${monster.slug}</code>
                            <button
                              class="btn btn-sm btn-outline-secondary py-0 px-2"
                              title="Copy Slug"
                              @click="${(/** @type {Event} */ e) => this._copyToClipboard(monster.slug, e)}"
                            >
                              ${clipboardIcon}
                            </button>
                          </div>

                          <div class="mb-2">
                            <div class="d-flex gap-1 mb-2">
                              <span class="badge bg-info">CR ${monster.cr}</span>
                              ${
                                monster.creatureType
                                  ? html`
                                    <span class="badge bg-secondary"
                                      >${this._capitalizeType(monster.creatureType)}</span
                                    >
                                  `
                                  : ''
                              }
                              <span class="badge bg-secondary">v${monster.version || 1}</span>
                              ${
                                monster.published
                                  ? html` <span class="badge bg-success">Published</span> `
                                  : html` <span class="badge bg-warning text-dark">Draft</span> `
                              }
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  `,
                )}
              </div>
            `
      }

      <!-- Delete Confirmation Modal -->
      ${
        this._showDeleteModal
          ? html`
            <div class="modal show d-block" style="background: rgba(0,0,0,0.5);">
              <div class="modal-dialog">
                <div class="modal-content">
                  <div class="modal-header">
                    <h5 class="modal-title">Delete Monster</h5>
                    <button type="button" class="btn-close" @click="${this._cancelDelete}"></button>
                  </div>
                  <div class="modal-body">
                    <p>Are you sure you want to delete this monster? This cannot be undone.</p>
                  </div>
                  <div class="modal-footer">
                    <button type="button" class="btn btn-secondary" @click="${this._cancelDelete}">Cancel</button>
                    <button type="button" class="btn btn-danger" @click="${this._confirmDelete}">Delete</button>
                  </div>
                </div>
              </div>
            </div>
          `
          : ''
      }
    `;
  }
}

customElements.define('staff-monsters-view', StaffMonstersView);
